import {
  Building2,
  Crown,
  Eye,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  StickyNote,
  Trash2,
  Upload,
  User,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  CampoDeBusqueda,
  ConfirmModal,
  DataTable,
  FilterBar,
  PageHeader,
  Paginacion,
  StatsCards,
  ViewToggle,
  type Vista,
} from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta } from "../../components/ui/primitivos";
import {
  borrarCliente,
  type Cliente,
  fetchCamposImportacion,
  fetchClientes,
} from "../../lib/consola-api";
import { type Orden, siguienteOrden } from "../../lib/orden";
import { paginar, paginaValida } from "../../lib/paginacion";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { contactoPrincipal, ordenarClientes } from "./clientes";
import { ImportarClientes } from "./ImportarClientes";
import { SinResultados, VistaLista, VistaTarjetas } from "./vistas";

/**
 * Clientes — porteada de `ClientList.tsx` y `ClientForm.tsx` de `imprenta`.
 *
 * La búsqueda va al servidor y no filtra en memoria: el listado viene acotado y
 * un filtro local mentiría sobre lo que hay más allá del límite.
 *
 * De allá vienen las **tres vistas** con preferencia persistida, las tarjetas
 * que filtran por tipo, y la **gestión de contactos** — que el detalle ya
 * devolvía y no se podía tocar desde ningún lado.
 */

const TIPOS = ["WHITE", "BLACK", "INTERNO"] as const;
const VISTAS: readonly Vista[] = ["tabla", "tarjetas", "lista"];
const CLAVE_PREFS = "listado-clientes";

function leerVista(): Vista {
  try {
    const guardado = localStorage.getItem(CLAVE_PREFS);
    if (guardado === "tabla" || guardado === "tarjetas" || guardado === "lista") return guardado;
  } catch {
    // Preferencia ilegible: se arranca con la tabla.
  }
  return "tabla";
}

export function ClientesPagina() {
  const navegar = useNavigate();
  const { can } = useSession();
  const [busqueda, setBusqueda] = useState("");
  /**
   * Lo que se le pidió al servidor, que va **detrás** de lo que se está
   * tipeando.
   *
   * Sin el botón «Buscar» (ADR 0095) la búsqueda es en vivo, y la consulta va al
   * SERVIDOR: una por tecla sería una consulta por tecla. El retardo la manda
   * recién cuando la persona para de escribir.
   *
   * No es polling (invariante 11): dispara **una vez**, por una acción, y no
   * hay nada latiendo cuando nadie toca el teclado.
   */
  const [aplicada, setAplicada] = useState("");
  const [tipo, setTipo] = useState("");
  const [vista, setVista] = useState<Vista>(leerVista);
  useEffect(() => {
    const t = setTimeout(() => setAplicada(busqueda.trim()), 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const clientes = useDatos(() => fetchClientes(aplicada), aplicada);
  const campos = useDatos(fetchCamposImportacion);

  useEffect(() => {
    localStorage.setItem(CLAVE_PREFS, vista);
  }, [vista]);

  /**
   * Abrir un cliente es NAVEGAR, no montar un modal (ADR 0064/0069).
   *
   * Con ruta propia el formulario tiene URL y los contactos entran en la misma
   * pantalla que los datos — en el modal solo aparecían al editar.
   */
  const abrirCliente = (c: Cliente) => navegar(`/clientes/${c.id}/editar`);
  const [importando, setImportando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orden, setOrden] = useState<Orden | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState(10);
  // Borrar un cliente pide confirmación, como allá. Antes bastaba UN click.
  const [aBorrar, setABorrar] = useState<Cliente | null>(null);

  const puedeCrear = can("clientes", "create");
  const puedeEditar = can("clientes", "update");
  const puedeBorrar = can("clientes", "delete");
  const puedeImportar = can("clientes", "import");

  const todos = clientes.datos ?? [];
  // El tipo filtra del lado cliente sobre lo que la búsqueda ya trajo: así las
  // tarjetas pueden mostrar el conteo de cada uno sin una consulta por tarjeta.
  const filtrados = tipo
    ? todos.filter((c) => (tipo === "VIP" ? c.esVip : c.tipoCliente === tipo))
    : todos;
  const visibles = ordenarClientes(filtrados, orden);
  /**
   * La página se recorta a lo que existe: se filtra estando en la 7 y el
   * listado pasa a tener 2, y sin esto la tabla queda vacía con el paginador
   * diciendo que hay filas — se lee como «el filtro no encontró nada».
   */
  const paginaActual = paginaValida(pagina, visibles.length, tamano);
  // Solo la TABLA pagina. En tarjetas y en lista se barre con el scroll, que
  // es lo que hace `imprenta`.
  const enPantalla = vista === "tabla" ? paginar(visibles, paginaActual, tamano) : visibles;

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await clientes.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Clientes"
        subtitulo="Quiénes compran: razón social, contacto y condición"
        acciones={
          <>
            <ViewToggle valor={vista} opciones={VISTAS} alCambiar={setVista} />
            {puedeImportar ? (
              <Boton variante="secundario" onClick={() => setImportando(true)}>
                <Upload className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Importar
              </Boton>
            ) : null}
            {puedeCrear ? (
              <Boton onClick={() => navegar("/clientes/nuevo")}>
                <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Nuevo Cliente
              </Boton>
            ) : null}
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {clientes.error ? <Aviso>{clientes.error}</Aviso> : null}

      <StatsCards
        estadisticas={[
          {
            titulo: "Todos",
            valor: todos.length,
            icono: Users,
            activa: tipo === "",
            alClickear: () => setTipo(""),
          },
          ...TIPOS.map((t) => ({
            titulo: t,
            valor: todos.filter((c) => c.tipoCliente === t).length,
            icono: Building2,
            activa: tipo === t,
            alClickear: () => setTipo(tipo === t ? "" : t),
          })),
          {
            titulo: "VIP",
            valor: todos.filter((c) => c.esVip).length,
            descripcion: "Cobran el precio VIP de la tarifa",
            icono: Crown,
            activa: tipo === "VIP",
            alClickear: () => setTipo(tipo === "VIP" ? "" : "VIP"),
          },
        ]}
      />

      <FilterBar
        filtrosActivos={(aplicada ? 1 : 0) + (tipo ? 1 : 0)}
        alLimpiar={() => {
          setBusqueda("");
          setAplicada("");
          setTipo("");
        }}
        busqueda={
          <CampoDeBusqueda
            valor={busqueda}
            alCambiar={setBusqueda}
            placeholder="Buscar por razón social, nombre de fantasía o CUIT..."
          />
        }
      />

      {clientes.cargando && todos.length === 0 ? (
        <SinResultados mensaje="Cargando..." />
      ) : visibles.length === 0 ? (
        <SinResultados
          mensaje={todos.length === 0 ? "No hay clientes" : "Nada coincide con ese filtro"}
        />
      ) : vista === "tarjetas" ? (
        <VistaTarjetas clientes={visibles} alAbrir={abrirCliente} />
      ) : vista === "lista" ? (
        <VistaLista clientes={visibles} alAbrir={abrirCliente} />
      ) : (
        <DataTable
          datos={enPantalla}
          clave={(c) => c.id}
          cargando={false}
          mensajeVacio="No hay clientes"
          orden={orden}
          alOrdenar={(campo) => setOrden((o) => siguienteOrden(o, campo))}
          columnas={[
            {
              clave: "razon",
              encabezado: "Razón social",
              ordenarPor: "razonSocial",
              className: "font-medium",
              render: (c) => (
                <>
                  {c.razonSocial}
                  {c.esVip ? (
                    <span className="ml-2">
                      <Etiqueta tono="aviso">VIP</Etiqueta>
                    </span>
                  ) : null}
                </>
              ),
            },
            {
              clave: "fantasia",
              encabezado: "Nombre fantasía",
              ordenarPor: "nombreFantasia",
              className: "text-neutral-600",
              render: (c) => c.nombreFantasia,
            },
            {
              clave: "cuit",
              encabezado: "CUIT",
              ordenarPor: "cuit",
              className: "font-mono text-neutral-600 text-xs",
              render: (c) => c.cuit ?? "—",
            },
            {
              clave: "tipo",
              encabezado: "Tipo",
              render: (c) => (
                <Etiqueta tono={c.tipoCliente === "INTERNO" ? "neutral" : "ok"}>
                  {c.tipoCliente}
                </Etiqueta>
              ),
            },
            {
              clave: "contacto",
              encabezado: "Contacto",
              ordenarPor: "contacto",
              // La columna de `imprenta`: quién es, cómo se lo ubica y cuántos
              // otros hay. Sin esto hay que abrir el cliente para saber a quién
              // llamar, que es justo lo que un listado evita.
              render: (c) => <CeldaContacto cliente={c} />,
            },
            {
              clave: "direccion",
              encabezado: "Dirección",
              className: "text-neutral-600",
              render: (c) =>
                c.direccion ? (
                  <span className="flex max-w-[14rem] items-center gap-1">
                    <MapPin aria-hidden="true" className="h-3 w-3 shrink-0 text-neutral-400" />
                    <span className="truncate">{c.direccion}</span>
                  </span>
                ) : (
                  "—"
                ),
            },
            {
              clave: "notas",
              encabezado: "Notas",
              // El popover de allá: la nota puede ser larga y no puede empujar
              // la fila, pero saber que EXISTE tiene que verse sin abrir nada.
              render: (c) =>
                c.observaciones ? (
                  <details className="group relative">
                    <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-1 text-neutral-600 text-xs hover:bg-neutral-100">
                      <StickyNote aria-hidden="true" className="h-3 w-3" />
                      Ver
                    </summary>
                    <div className="absolute z-10 mt-1 w-64 rounded-md border border-neutral-200 bg-white p-3 shadow-lg">
                      <p className="mb-2 font-medium text-neutral-500 text-xs">Notas del cliente</p>
                      <p className="max-h-40 overflow-y-auto whitespace-pre-wrap text-neutral-800 text-sm">
                        {c.observaciones}
                      </p>
                    </div>
                  </details>
                ) : (
                  <span className="text-neutral-400">—</span>
                ),
            },
            {
              clave: "acciones",
              encabezado: "",
              className: "text-right",
              render: (c) => (
                <div className="flex justify-end gap-1">
                  {/* Sin texto, como `imprenta` (ADR 0091): el nombre va en
                      `aria-label` y en `title`, que son obligatorios acá. El
                      ícono cambia con el permiso — un lápiz sobre algo que solo
                      se puede mirar promete algo que no pasa. */}
                  <Boton
                    variante="fantasma"
                    tamano="icono"
                    aria-label={`${puedeEditar ? "Editar" : "Ver"} ${c.razonSocial}`}
                    title={puedeEditar ? "Editar" : "Ver"}
                    onClick={() => abrirCliente(c)}
                  >
                    {puedeEditar ? (
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </Boton>
                  {puedeBorrar ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Eliminar ${c.razonSocial}`}
                      title="Eliminar"
                      onClick={() => setABorrar(c)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      )}

      {vista === "tabla" && visibles.length > 0 ? (
        <Paginacion
          pagina={paginaActual}
          tamano={tamano}
          total={visibles.length}
          alCambiarPagina={setPagina}
          alCambiarTamano={(t) => {
            setTamano(t);
            setPagina(1);
          }}
        />
      ) : null}

      <ConfirmModal
        abierto={aBorrar !== null}
        onCerrar={() => setABorrar(null)}
        onConfirmar={() => {
          const cliente = aBorrar;
          if (cliente) void accion(() => borrarCliente(cliente.id));
        }}
        titulo="Eliminar cliente"
        descripcion={`¿Eliminar ${aBorrar?.razonSocial ?? ""}? Esta acción no se puede deshacer.`}
        textoConfirmar="Eliminar"
        variante="destructiva"
      />

      {importando ? (
        <ImportarClientes
          campos={campos.datos ?? []}
          onCerrar={() => setImportando(false)}
          onImportado={() => clientes.recargar()}
        />
      ) : null}
    </div>
  );
}

/**
 * El contacto principal en la fila, con su mail o su teléfono y cuántos otros
 * hay — la celda «Contacto» de `ClientList.tsx`.
 */
function CeldaContacto({ cliente }: { cliente: Cliente }) {
  const principal = contactoPrincipal(cliente.contactos);
  if (!principal) return <span className="text-neutral-400 text-xs italic">Sin contactos</span>;

  const otros = cliente.contactos.length - 1;
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1">
        <User aria-hidden="true" className="h-3 w-3 shrink-0 text-neutral-400" />
        <span className="max-w-[10rem] truncate">{principal.nombre ?? "Sin nombre"}</span>
        {otros > 0 ? <span className="text-neutral-500 text-xs">(+{otros})</span> : null}
      </div>
      {principal.email ? (
        <div className="flex items-center gap-1 text-neutral-500 text-xs">
          <Mail aria-hidden="true" className="h-2.5 w-2.5 shrink-0" />
          <span className="max-w-[10rem] truncate">{principal.email}</span>
        </div>
      ) : null}
      {principal.telefono ? (
        <div className="flex items-center gap-1 text-neutral-500 text-xs">
          <Phone aria-hidden="true" className="h-2.5 w-2.5 shrink-0" />
          <span>{principal.telefono}</span>
        </div>
      ) : null}
    </div>
  );
}
